import React from 'react';
import ReactDOM from 'react-dom';
import {view} from '@sail/ui';

/**
 *  createSlots returns a typed Slots + Slot pair
 */
// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const createSlots = <SlotNames extends string>(
  slotNames: SlotNames[],
) => {
  type ISlots = {
    [key in SlotNames]?: HTMLDivElement | null;
  };

  type ContextProps = {
    slots: ISlots;
    registerSlot: ({
      name,
      portalTarget,
    }: {
      name: SlotNames;
      portalTarget: HTMLDivElement | null;
    }) => void;
    hasProvider: boolean;
  };
  const SlotsContext = React.createContext<ContextProps>({
    slots: {},
    registerSlot: () => {},
    hasProvider: false,
  });

  /**
   *  A Slot component registers itself with the Slots component.
   *  When all the Slot components have mounted, i.e. registered themselves in the Slots component,
   *  we re-render the Slots component.
   */
  const Slots: React.FC = ({children}) => {
    const [slots, setSlots] = React.useState<ISlots>({});
    const registerSlot = ({
      name,
      portalTarget,
    }: {
      name: SlotNames;
      portalTarget: HTMLDivElement | null;
    }) => {
      if (slots[name] !== portalTarget) {
        setSlots((prev) => ({
          ...prev,
          [name]: portalTarget,
        }));
      }
    };

    return (
      <SlotsContext.Provider value={{slots, registerSlot, hasProvider: true}}>
        {children}
      </SlotsContext.Provider>
    );
  };

  function Slot({name}: {name: (typeof slotNames)[number]}) {
    const {registerSlot} = React.useContext(SlotsContext);
    return (
      <view.div
        ref={(ref: HTMLDivElement | null) =>
          registerSlot({name, portalTarget: ref})
        }
      />
    );
  }

  function SlotContent(props: {name: SlotNames; children: React.ReactNode}) {
    const {name, children} = props;
    const {hasProvider, slots} = React.useContext(SlotsContext);
    const portalTarget = slots[name];
    return portalTarget && hasProvider ? (
      ReactDOM.createPortal(children, portalTarget)
    ) : (
      <>{children}</>
    );
  }

  return {Slots, Slot, SlotContent};
};
